/**
 * L'écran de résultat d'une **vidéo jalon** (§9.3, PLAN §4.13).
 *
 * ── Pourquoi ce n'est pas `Publication.tsx` ───────────────────────────────────
 *
 * Parce qu'un jalon **ne crée aucun `VideoRecord`**, et c'est une décision du moteur, pas un
 * oubli : *« effets exprimés en % des abonnés actuels, jamais en valeurs absolues » — un jalon
 * n'a donc pas de `VuesPic`, pas de courbe, pas de taux de conversion : le % EST l'effet*
 * (`shared/src/engine/jalons.ts`). Il n'y a donc ni courbe à tracer, ni classe à révéler, ni
 * coulisses de prévisions : le résultat est **immédiat et total**, et l'écran doit le dire
 * comme tel.
 *
 * ⟦C57 · lot 8 · A-174⟧ **Moins de texte** : le nom du jalon, une phrase, les quatre nombres (abonnés, effet,
 * score, PC), une ligne sur ce que ça change. Ce que disait la note du bas (un pourcentage déjà encaissé ;
 * ou l'effet divisé, le jalon consommé) est derrière « Les dessous », mot pour mot.
 *
 * ── Tout vient de l'événement `jalon_done` ────────────────────────────────────
 *
 * Rien n'est recalculé ici : le moteur a poussé son `jalon_done` avec le score d'exécution, le
 * pourcentage **effectivement appliqué** (déjà divisé par deux en cas d'échec), les abonnés
 * gagnés et les PC. C'est le même contrat dans les deux modes — en
 * mode compte l'événement traverse `POST /actions`, en bac à sable il sort de `resolve()`.
 */

import { JALON_EFFET_RATE, SEUIL_JALON_REUSSI } from "@maxime/shared";

import { MotDeNote } from "../../atelier/core/NoteSur100";
import { DessousBloc, LesDessous } from "../../components/LesDessous";
import { nombre, points } from "../../game/format";

/** Ce que l'écran lit de `jalon_done` — un miroir strict des clés poussées par `jouerJalon`. */
export interface FaitJalon {
  nom: string;
  reussi: boolean;
  execution_score: number;
  subs_pct: number;
  abonnes_gagnes: number;
  pc_gagnes: number;
}

/**
 * Extrait le fait du tour, s'il y en a un. Rend `null` quand l'action n'a produit aucun
 * `jalon_done` — cas qui ne devrait pas arriver, mais qui vaut mieux qu'un écran inventé.
 */
export function faitJalon(
  events: readonly { kind: string; data: Record<string, number | string | boolean | null> }[],
): FaitJalon | null {
  const e = events.find((ev) => ev.kind === "jalon_done");
  if (!e) return null;
  const d = e.data;
  const nb = (cle: string) => (typeof d[cle] === "number" ? (d[cle] as number) : 0);
  return {
    nom: typeof d.nom === "string" ? d.nom : "cette vidéo jalon",
    reussi: d.reussi === true,
    execution_score: nb("execution_score"),
    subs_pct: nb("subs_pct"),
    abonnes_gagnes: nb("abonnes_gagnes"),
    pc_gagnes: nb("pc_gagnes"),
  };
}

/** « +12,4 % » — un pourcentage signé, à une décimale, à la française. */
function pourcent(v: number): string {
  return `${v >= 0 ? "+" : "−"}${Math.abs(v).toFixed(1).replace(".", ",")} %`;
}

export function ResultatJalon({ fait, onFermer }: { fait: FaitJalon; onFermer(): void }) {
  return (
    <section className="carte scotche resultat">
      <p className="etiquette">{fait.reussi ? "un jalon de plus" : "pas au niveau"}</p>
      <h2 className="resultat-titre">
        {fait.reussi ? "🏆" : "😬"} {fait.nom}
      </h2>
      <p className="resultat-sujet">
        {fait.reussi ? "Cette vidéo-là, on s'en souviendra." : `Il fallait ${SEUIL_JALON_REUSSI} de moyenne.`}
      </p>

      <dl className="resultat-chiffres">
        <div>
          <dt>Abonnés</dt>
          <dd className={fait.abonnes_gagnes > 0 ? "entoure" : ""}>
            {fait.abonnes_gagnes > 0 ? "+" : ""}
            {nombre(fait.abonnes_gagnes)}
          </dd>
        </div>
        <div>
          <dt>Effet appliqué</dt>
          <dd>{pourcent(fait.subs_pct)}</dd>
        </div>
        <div>
          <dt>Score d'exécution</dt>
          {/* ⟦C62 · friction 7⟧ Une note sur 100, et son mot. */}
          <dd>
            {Math.round(fait.execution_score)}
            <span className="note-sur"> / 100</span>
          </dd>
          <MotDeNote note={fait.execution_score} />
        </div>
        <div>
          <dt>Points de chaîne</dt>
          <dd>
            {fait.pc_gagnes > 0 ? "+" : ""}
            {points(fait.pc_gagnes)}
          </dd>
        </div>
      </dl>

      {/* ⟦C57 · lot 8⟧ UNE ligne : ce que l'effet est devenu. Les phrases d'avant sont dans « Les dessous ». */}
      <p className="resultat-note">
        {fait.reussi ? (
          "Effet encaissé en entier, sans courbe."
        ) : (
          <>
            {/* La part conservée vient de `JALON_EFFET_RATE` : si Mathieu la change (elle est
                sur la table — cf. `docs/AUDIT-EQUILIBRAGE.md`), cette phrase suit. */}
            Effet divisé par {Math.round(1 / JALON_EFFET_RATE)}, jalon consommé.
          </>
        )}
      </p>

      <div className="jeu-actions">
        <button className="bouton primaire grand" type="button" onClick={onFermer}>
          Retour au studio
        </button>
        <LesDessous titre={fait.nom}>
          <DessousBloc titre={fait.reussi ? "Un jalon encaissé" : "Un jalon raté"}>
            {fait.reussi ? (
              <p className="dessous-texte">
                L'effet d'un jalon est un <strong>pourcentage de tes abonnés du moment</strong> : il
                est déjà encaissé, en entier, il n'y a pas de courbe à attendre.
              </p>
            ) : (
              <>
                <p className="dessous-texte">
                  Il fallait {SEUIL_JALON_REUSSI} de moyenne. Le public a vu la vidéo quand même.
                </p>
                <p className="dessous-texte">
                  L'effet a été <strong>divisé par {Math.round(1 / JALON_EFFET_RATE)}</strong> et aucun
                  point de jalon n'est crédité. Ce jalon est <strong>consommé</strong> : il ne se retente
                  pas, on ne refait pas sa première face cam trois fois.
                </p>
              </>
            )}
          </DessousBloc>
        </LesDessous>
      </div>
    </section>
  );
}
